<template>
  <LmDialog v-model="dialog" :styles="{ minWidth: '900px', minHeight: '440px', background: '#65b5a5' }">
    <template v-if="dialog" #toolbar>
      <TodoToolbar :edit-item="editItem" :title.sync="title" @close="dialog = false" />
    </template>

    <template v-if="dialog" #content>
      <div class="dialog-content">
        <div class="left section">
          <q-editor
            ref="editor"
            v-model.trim="body"
            :content-style="{ listStylePosition: 'inside' }"
            rows="1"
            toolbar-text-color="grey"
            toolbar-toggle-color="tertiary"
            toolbar-flat
            @paste.native="Q_EDITOR_MIXIN_onPaste"
          />

          <CheckList ref="checkList" :from-page="fromPage" :edit-item="editItem" :checklists="checklists" />

          <Attachments ref="attachments" :from-page="fromPage" :edit-item="editItem" :media="media" />

          <Comments
            v-if="editItem && fromPage !== 'templates'"
            :from-page="fromPage"
            :edit-item="editItem"
            :comments="comments"
          />
        </div>

        <div class="right section">
          <RightSection
            ref="rightSection"
            :from-page="fromPage"
            :state-id="stateId"
            :title="title"
            :edit-item="editItem"
            :disable-save-as-template="disableSaveAsTemplate"
            @save="handleSave"
            @save-as-template="saveAsTemplate"
            @delete="onDelete"
          />
        </div>
      </div>
      <SpinnerAudioLoader :loading="loading" />
    </template>
  </LmDialog>
</template>

<script>
import * as modularApi from '@/modular-api'
import * as apiTo from './api'
import { mapState, mapMutations, mapActions } from 'vuex'
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import Q_EDITOR_MIXIN from '@/mixins/q-editor-mixin'
import { stripHtmlFromInput } from '@lib/formatting.utils'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import CheckList from './components/CheckList'
import Attachments from './components/Attachments'
import Comments from './components/Comments'
import RightSection from './components/RightSection'
import SpinnerAudioLoader from '@/reusable/base-components/SpinnerAudioLoader'
import TodoToolbar from './components/TodoToolbar'

export default {
  components: {
    LmDialog,
    CheckList,
    Attachments,
    Comments,
    RightSection,
    SpinnerAudioLoader,
    TodoToolbar
  },

  mixins: [Q_EDITOR_MIXIN],

  props: {
    value: Boolean,
    editItem: Object,
    stateId: Number,
    statesWithTasks: Array,
    fromPage: String // from what page the modal is used eg [dashboard, subscription, kanban]
  },

  data: () => ({
    loading: false,
    disableSaveAsTemplate: false,
    title: '',
    body: '',
    checklists: null,
    comments: null,
    media: null
  }),

  computed: {
    ...mapState('todo-modal', ['createData', 'subscription', 'selectedTemplate']),

    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },

  watch: {
    value(val) {
      val && this.getCreateData() // get new data everytime we open the modal
      if (val && this.editItem) this.getTodoDetails(this.editItem.id)
      if (!val) {
        this.checklists = null
        this.comments = null
        this.media = null
        this.body = ''
      } else this.disableSaveAsTemplate = false
    },

    selectedTemplate(template) {
      this.body = template.body || ''
    }
  },

  methods: {
    ...mapActions('todo-modal', ['getAndSetCreateData']),
    ...mapMutations('todo-modal', ['addTemplate']),

    async getCreateData() {
      this.loading = true
      await this.getAndSetCreateData(this.fromPage)
      this.loading = false
      if (this.editItem) this.updateFields(this.editItem)
    },

    getTodoDetails(todoId) {
      apiTo.getTodoDetails(todoId).then(({ data }) => {
        const { checklist_items, comments, media } = data.result
        this.checklists = checklist_items
        this.comments = comments
        this.media = media
      })
    },

    updateFields(item) {
      this.title = item.title
      this.body = item.body || ''
      this.$nextTick(() => this.$refs.rightSection.updateFields(item))
    },

    handleSave(payload, subscriberIds) {
      if (this.editItem) this.generatePayloadForUpdate(payload, subscriberIds)
      else this.generatePayloadForCreate(payload)
    },

    saveAsTemplate(payload) {
      const pos = this.getKanbanPositionForNewTodo(payload)
      const files = { ...this.$refs.attachments.getPayload() }
      const extendedPayload = {
        ...payload,
        title: this.title,
        body: stripHtmlFromInput(this.body),
        kanban_position: pos,
        checklist_items: this.$refs.checkList.getPayload(),
        media_from_template: files.media_from_template
      }
      const apiData = this.getFormData(extendedPayload, files.media, true)
      this.disableSaveAsTemplate = true
      this.loading = true
      apiTo
        .todoApi('post', '/to-dos', apiData)
        .then(({ data: { result: newTemplate } }) => this.addTemplate(newTemplate))
        .catch(() => (this.disableSaveAsTemplate = false))
        .finally(() => (this.loading = false))
    },

    async generatePayloadForUpdate(payload, subscriberIds) {
      if (subscriberIds) {
        await this.subscribeTodo(subscriberIds)
      }

      let pos = this.editItem.kanban_position
      const taskState = this.editItem.state ? this.editItem.state.id : null
      if (this.fromPage !== 'kanban') pos = null
      else if (payload.state_id && payload.state_id !== taskState) {
        const state = this.statesWithTasks.find(s => s.id === payload.state_id)
        if (state) {
          if (state.tasks.length) pos = 65535 + state.tasks[state.tasks.length - 1].kanban_position
          else pos = 65535
        } else {
          // todo moved to another state in another board
          pos = null
        }
      }
      payload.is_template = this.fromPage === 'templates'

      this.updateTodo({
        ...payload,
        title: this.title,
        body: stripHtmlFromInput(this.body),
        kanban_position: pos
      })
    },

    generatePayloadForCreate(payload) {
      const pos = this.getKanbanPositionForNewTodo(payload)
      const files = { ...this.$refs.attachments.getPayload() }
      const extendedPayload = {
        ...payload,
        title: this.title,
        body: stripHtmlFromInput(this.body),
        kanban_position: pos,
        checklist_items: this.$refs.checkList.getPayload(),
        media_from_template: files.media_from_template
      }

      const apiData = this.getFormData(extendedPayload, files.media)
      this.createTodo(apiData)
    },

    getKanbanPositionForNewTodo(payload) {
      let pos = 65535
      if (this.fromPage !== 'kanban') pos = null
      else if (payload.state_id) {
        const state = this.statesWithTasks.find(s => s.id === payload.state_id)
        if (state.tasks.length) pos += state.tasks[state.tasks.length - 1].kanban_position
      }
      return pos
    },

    getFormData(data, media, isTemplate = false) {
      let formDataSource = new LmFormObject(data)
      delete formDataSource.errors
      delete formDataSource.originalData
      let formData = new FormData()
      formDataSource.getFormData(formData, data)
      Array.from(media).forEach((file, index) => formData.append(`media[${index}]`, file))
      formData.append('is_template', this.fromPage === 'templates' || isTemplate)
      return formData
    },

    updateTodo(payload) {
      this.loading = true

      apiTo
        .todoApi('put', `/to-dos/${this.editItem.id}`, payload)
        .then(({ data }) => {
          this.$emit('updated-todo', data.result)
          this.resetModal()
        })
        .finally(() => (this.loading = false))
    },

    subscribeTodo(subscriberIds) {
      this.loading = true

      return modularApi
        .SUBSCRIBE({
          entity: 'to_do',
          entity_id: this.editItem.id,
          user_ids: subscriberIds
        })
        .catch(() => (this.loading = false))
    },

    createTodo(payload) {
      this.loading = true
      apiTo
        .todoApi('post', '/to-dos', payload)
        .then(({ data }) => {
          this.$emit('new-todo', data.result)
          this.resetModal()
        })
        .finally(() => (this.loading = false))
    },

    resetModal() {
      this.dialog = false
      Object.assign(this.$data, this.$options.data.apply(this))
    },

    onDelete() {
      this.dialog = false
      this.$emit('delete')
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'
styledScrollFor('.dialog-content')

.dialog-content {
  display grid
  grid-template-columns 2fr 1fr
  gap 5px
  overflow auto
  min-height calc(100vh - 200px)
  max-height calc(100vh - 200px)

  > .section {
    display grid
    gap 10px
    align-items start
    align-content start
    padding 15px
  }

  > .left.section {
    background white
  }

  > .right.section {
    background #f5f5f5
  }
}
</style>
